import React, { useState, useEffect, useRef } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { AboutHeroParagraph, AboutPersonalParagraphs, AboutFunFactsList } from './AboutContent';

import scenicPhoto from '../../../assets/scenic_view.webp';
import beachPhoto from '../../../assets/beach.webp';
import chowPhoto from '../../../assets/chow.webp';
import drawingPhoto from '../../../assets/drawing.webp';
import familyPhoto from '../../../assets/family.webp';

import scenicPhotoPng from '../../../assets/scenic_view.png';
import beachPhotoPng from '../../../assets/beach.png';
import chowPhotoPng from '../../../assets/chow.png';
import drawingPhotoPng from '../../../assets/drawing.png';
import familyPhotoPng from '../../../assets/family.png';

const PHOTOS = [
  { src: scenicPhoto,  fallback: scenicPhotoPng,  alt: 'Hiking in Yosemite', pos: 'center center' },
  { src: beachPhoto,   fallback: beachPhotoPng,   alt: 'At the beach',        pos: 'center top'    },
  { src: chowPhoto,    fallback: chowPhotoPng,    alt: 'With my cat Chow',    pos: 'center center' },
  { src: drawingPhoto, fallback: drawingPhotoPng, alt: 'My artwork',          pos: 'center top'    },
  { src: familyPhoto,  fallback: familyPhotoPng,  alt: 'Family trip',         pos: 'center top'    },
];

const INTERVAL_MS = 4000;
const FADE_MS = 600;

interface InlineAboutSectionProps {
  isLoaded: boolean;
}

const InlineAboutSection: React.FC<InlineAboutSectionProps> = ({ isLoaded }) => {
  const { ref, isVisible } = useScrollReveal<HTMLElement>();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (prefersReducedMotion) return;

    intervalRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrentIdx((prev) => (prev + 1) % PHOTOS.length);
        setFading(false);
      }, FADE_MS);
    }, INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [prefersReducedMotion]);

  const photo = PHOTOS[currentIdx];

  return (
    <section
      ref={ref}
      className={`inline-about-section scroll-reveal ${isVisible ? 'is-visible' : ''} ${isLoaded ? 'animate-in' : ''}`}
      aria-labelledby="inline-about-heading"
    >
      <div className="inline-about-grid">
        <div className="inline-about-photo">
          <picture>
            <source srcSet={photo.src} type="image/webp" />
            <img
              src={photo.fallback}
              alt={photo.alt}
              loading="lazy"
              className={`inline-about-img ${fading ? 'inline-about-img--fading' : ''}`}
              style={{ objectPosition: photo.pos }}
            />
          </picture>
        </div>
        <div className="inline-about-content">
          <h2 id="inline-about-heading">About Me</h2>
          <AboutHeroParagraph />
          <AboutPersonalParagraphs />
          <AboutFunFactsList />
        </div>
      </div>
    </section>
  );
};

export default React.memo(InlineAboutSection);
